import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/terms")({ component: TermsPage });

// TODO: Update entity name and jurisdiction once incorporated
const ENTITY_NAME = "Crescend";
const EFFECTIVE_DATE = "March 9, 2026";

function TermsPage() {
	return (
		<div className="pt-24 pb-16 lg:pb-24">
			<div className="max-w-3xl mx-auto px-6 lg:px-12">
				<h1 className="font-display text-display-md text-cream mb-4">
					Terms of Service
				</h1>
				<p className="text-body-sm text-text-tertiary mb-12">
					Effective: {EFFECTIVE_DATE}
				</p>

				<div className="space-y-10 text-body-md text-text-secondary leading-relaxed">
					<Section title="1. Acceptance of Terms">
						<p>
							By accessing or using the {ENTITY_NAME} service ("Service"),
							including our website, web application, and iOS application, you
							agree to be bound by these Terms of Service ("Terms"). If you do
							not agree, do not use the Service.
						</p>
					</Section>

					<Section title="2. Description of Service">
						<p>
							{ENTITY_NAME} is a piano practice companion that uses audio
							analysis to provide feedback on musical expression. The Service
							records audio of piano performances, analyzes them using machine
							learning models, and delivers feedback through an AI-powered
							teacher interface.
						</p>
					</Section>

					<Section title="3. User Accounts">
						<p>
							To use certain features, you must create an account via Sign in
							with Apple. You are responsible for maintaining the security of
							your account and for all activity under it. You must be at least
							13 years old to create an account.
						</p>
					</Section>

					<Section title="4. Acceptable Use">
						<p>You agree not to:</p>
						<ul className="list-disc pl-6 mt-3 space-y-2">
							<li>
								Use the Service for any unlawful purpose or in violation of
								these Terms
							</li>
							<li>
								Attempt to reverse-engineer, decompile, or extract the source
								code of the Service or its models
							</li>
							<li>
								Interfere with or disrupt the Service or its infrastructure
							</li>
							<li>
								Upload content that infringes on the intellectual property
								rights of others
							</li>
							<li>Use the Service to develop competing products or services</li>
							<li>
								Resell, sublicense, or commercially redistribute the Service
								without written permission
							</li>
						</ul>
					</Section>

					<Section title="5. Audio Recordings">
						<p>
							The Service records audio during practice sessions to provide
							feedback. By using the recording features, you consent to the
							capture and processing of audio. You represent that you have the
							right to record any audio captured through the Service and that
							recordings do not violate the rights of any third party.
						</p>
					</Section>

					<Section title="6. Intellectual Property">
						<p>
							The Service, including its models, algorithms, software, and
							content, is owned by {ENTITY_NAME} and protected by intellectual
							property laws. Your audio recordings remain your property. By
							using the Service, you grant {ENTITY_NAME} a limited license to
							process your recordings solely to provide the Service.
						</p>
					</Section>

					<Section title="7. Feedback and AI-Generated Content">
						<p>
							The feedback provided by the Service is generated by AI models and
							is intended as a practice aid, not as a substitute for
							professional music instruction. {ENTITY_NAME} does not guarantee
							the accuracy, completeness, or suitability of any feedback.
						</p>
					</Section>

					<Section title="8. Disclaimers">
						<p>
							THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
							WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT
							LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
							PURPOSE, AND NON-INFRINGEMENT. {ENTITY_NAME.toUpperCase()} DOES
							NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR
							SECURE.
						</p>
					</Section>

					<Section title="9. Limitation of Liability">
						<p>
							TO THE MAXIMUM EXTENT PERMITTED BY LAW,{" "}
							{ENTITY_NAME.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT,
							INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY
							LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR
							INDIRECTLY, ARISING OUT OF YOUR USE OF THE SERVICE.{" "}
							{ENTITY_NAME.toUpperCase()}'S TOTAL LIABILITY SHALL NOT EXCEED THE
							AMOUNTS PAID BY YOU TO {ENTITY_NAME.toUpperCase()} IN THE TWELVE
							MONTHS PRECEDING THE CLAIM.
						</p>
					</Section>

					<Section title="10. Termination">
						<p>
							We may suspend or terminate your access to the Service at any time
							for any reason, including violation of these Terms. You may
							terminate your account at any time by contacting us. Upon
							termination, your right to use the Service ceases immediately.
						</p>
					</Section>

					<Section title="11. Changes to Terms">
						<p>
							We may update these Terms from time to time. We will notify you of
							material changes by posting the updated Terms on the Service with
							a new effective date. Continued use of the Service after changes
							constitutes acceptance of the revised Terms.
						</p>
					</Section>

					<Section title="12. Governing Law">
						<p>
							These Terms are governed by the laws of the State of Delaware,
							without regard to its conflict of law provisions. Any disputes
							arising from these Terms or the Service shall be resolved in the
							courts located in Delaware.
						</p>
					</Section>
				</div>
			</div>
		</div>
	);
}

function Section({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<section>
			<h2 className="font-display text-display-sm text-cream mb-4">{title}</h2>
			{children}
		</section>
	);
}
